import { useState, useEffect } from "react";
import {
  useCreateBrand,
  useOneBrand,
  useUpdateBrand,
} from "http-hooks/brand/useBrand";
import { Switch } from "@headlessui/react";
import Input from "components/ui/Input";
import Alert from "components/ui/Alert";
import Modal from "components/ui/modals";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function SetBrand({ id, onClose, open }) {
  const [brandDetails, setBrandDetails] = useState({
    brandname: "",
    boolbrandshow: 0,
    ag_status: 1,
    parentid: "0",
  });

  const { isLoading: isLoadingBrand, data } = useOneBrand(id, !!id);
  const { mutateAsync: mutateCreate, isPending: isCreating } = useCreateBrand();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useUpdateBrand();

  const isPending = isCreating || isUpdating;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBrandDetails((prevData) => ({ ...prevData, [name]: value }));
  };

  const getStatusChangeMessage = () => {
    if (brandDetails.ag_status === 0) {
      return "Warning: Changing status to inactive will make all items under this brand inactive.";
    }
    return "Warning: Changing status to active will make all items under this brand active.";
  };

  useEffect(() => {
    if (data) {
      setBrandDetails((prev) => {
        const updatedDetails = {};
        Object.keys(prev).forEach((key) => {
          updatedDetails[key] = data[key] ?? prev[key];
        });
        return updatedDetails;
      });
    }
  }, [data]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (id) {
        await mutateUpdate({ id, data: brandDetails });
      } else {
        await mutateCreate(brandDetails);
      }
      onClose();
    } catch (error) {
      console.error("Operation failed:", error);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {id ? "Update" : "Add"} Brand
        </h3>
      }
      footer={
        <div className="flex justify-end gap-x-3">
          <button
            type="button"
            className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="set-brand-form"
            disabled={isPending}
            className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Saving..." : "Save"}
          </button>
        </div>
      }
    >
      {id && isLoadingBrand ? (
        <div className="animate-pulse space-y-6">
          <div className="h-9 bg-gray-200 rounded w-full" />
          <div className="h-6 bg-gray-200 rounded w-12" />
          <div className="h-6 bg-gray-200 rounded w-12" />
        </div>
      ) : (
        <form id="set-brand-form" onSubmit={handleSubmit} className="space-y-6">
          {data && brandDetails.ag_status !== data.ag_status && (
            <Alert
              variant="warning"
              title="Status Change Warning"
              description={getStatusChangeMessage()}
            />
          )}
          <Input
            type="text"
            name="brandname"
            id="brandname"
            label="Brand Name"
            maxLength="40"
            value={brandDetails.brandname}
            onChange={handleInputChange}
          />
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Brand Show
              </label>
              <Switch
                checked={!!brandDetails.boolbrandshow}
                onChange={(e) => setBrandDetails((prev) => ({ ...prev, boolbrandshow: e ? 1 : 0 }))}
                className={classNames(
                  brandDetails.boolbrandshow ? "bg-indigo-600" : "bg-gray-200",
                  "relative inline-flex ml-1 mt-3 h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2"
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    brandDetails.boolbrandshow ? "translate-x-5" : "translate-x-0",
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </Switch>
            </div>
            {id && (
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Status
                </label>
                <Switch
                  checked={!!brandDetails.ag_status}
                  onChange={(e) => setBrandDetails((prev) => ({ ...prev, ag_status: e ? 1 : 0 }))}
                  className={classNames(
                    brandDetails.ag_status ? "bg-indigo-600" : "bg-gray-200",
                    "relative inline-flex ml-1 mt-3 h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2"
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      brandDetails.ag_status ? "translate-x-5" : "translate-x-0",
                      "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    )}
                  />
                </Switch>
              </div>
            )}
          </div>
        </form>
      )}
    </Modal>
  );
}
